import React from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import SponsorStrip from "./components/SponsorStrip";
import DisclaimerBanner from "./components/DisclaimerBanner";

import Home from "./pages/Home";
import AbuserDetail from "./pages/AbuserDetail";
import ReportAbuse from "./pages/ReportAbuse";
import NearbyStations from "./pages/NearbyStations";
import About from "./pages/About";
import Partnerships from "./pages/Partnerships";
import Donate from "./pages/Donate";
import Admin from "./pages/Admin";
import Legal from "./pages/Legal";
import EditorialStandards from "./pages/EditorialStandards";
import WhereYourMoneyGoes from "./pages/WhereYourMoneyGoes";
import Advertise from "./pages/Advertise";
import MyCampaigns from "./pages/MyCampaigns";
import ThankYou from "./pages/ThankYou";
import Sponsors from "./pages/Sponsors";
import AuthCallback from "./pages/AuthCallback";
import DonateThanks from "./pages/DonateThanks";
import InMemoryOf from "./pages/InMemoryOf";
import AnimalBanLookup from "./pages/AnimalBanLookup";
import RescuePartners from "./pages/RescuePartners";
import LocalShelters from "./pages/LocalShelters";
import ActiveCases from "./pages/ActiveCases";
import ActiveCaseDetail from "./pages/ActiveCaseDetail";
import UrgentRescues from "./pages/UrgentRescues";
import UrgentRescueDetail from "./pages/UrgentRescueDetail";
import AnnouncementStrip from "./components/AnnouncementStrip";

import { AuthProvider } from "./lib/auth";
import { trackPageView } from "./lib/analytics";

// Detect Emergent Auth callback (#session_id=...) at the top level so it
// runs synchronously during render — useEffect would be too late and could
// race with route-level auth checks.
function AppRouter() {
  const location = useLocation();

  // Fire a page-view on every route change. The admin route is excluded so
  // we don't pollute the funnel with operator traffic.
  React.useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    trackPageView(location.pathname);
  }, [location.pathname]);

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/active-cases" element={<ActiveCases />} />
      <Route path="/active-cases/:caseId" element={<ActiveCaseDetail />} />
      <Route path="/urgent-rescues" element={<UrgentRescues />} />
      <Route path="/shelters" element={<Navigate to="/local-shelters" replace />} />
      <Route path="/urgent-rescues/:rescueId" element={<UrgentRescueDetail />} />
      <Route path="/abuser/:id" element={<AbuserDetail />} />
      <Route path="/report" element={<ReportAbuse />} />
      <Route path="/nearby" element={<NearbyStations />} />
      <Route path="/about" element={<About />} />
      <Route path="/partnerships" element={<Partnerships />} />
      <Route path="/donate" element={<Donate />} />
      <Route path="/legal" element={<Legal />} />
      <Route path="/editorial" element={<EditorialStandards />} />
      <Route path="/where-your-money-goes" element={<WhereYourMoneyGoes />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/advertise" element={<Advertise />} />
      <Route path="/my-campaigns" element={<MyCampaigns />} />
      <Route path="/thanks/:id" element={<ThankYou />} />
      <Route path="/sponsors" element={<Sponsors />} />
      <Route path="/donate/thanks" element={<DonateThanks />} />
      <Route path="/in-memory" element={<InMemoryOf />} />
      <Route path="/animal-ban-lookup" element={<AnimalBanLookup />} />
      <Route path="/rescue-partners" element={<RescuePartners />} />
      <Route path="/local-shelters" element={<LocalShelters />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
            <AnnouncementStrip />
            <DisclaimerBanner />
            <Navbar />
            <main className="min-h-[60vh]">
              <AppRouter />
            </main>
            <SponsorStrip />
            <Footer />
            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  background: "#09090b",
                  color: "#ffffff",
                  border: "1px solid #dc2626",
                  borderRadius: 0,
                  fontFamily: "IBM Plex Sans, sans-serif",
                },
              }}
            />
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}
